/**
 * FaceTemplate.tsx — Face WebScan.
 * Covers: face_search, photo_searcher.
 *
 * Score-driven. Anything under 80% renders amber and is labelled "possible",
 * because a 65% hit against a 1.39-billion-face index is a lead, not a match.
 * The old popup showed "Not found:" followed by results, which read as a
 * contradiction.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { networkMeta, radii } from '../tokens'
import { NoData, Photo, RawCard, ResCard, Stats, usePhotoLightbox, Warn, useTokens } from '../ui/primitives'
import type { TemplateProps } from '../registry'

type Match = { score: number; image?: string; url?: string; name?: string; index?: number; guid?: string }

const band = (s: number) =>
  s >= 80 ? { colour: '#10B981', label: 'strong' } : s >= 65 ? { colour: '#F59E0B', label: 'possible' } : { colour: '#94A3B8', label: 'weak' }

const networkFromUrl = (url?: string) => {
  if (!url) return ''
  if (/instagram\.com/i.test(url)) return 'INSTAGRAM'
  if (/facebook\.com/i.test(url)) return 'FB'
  if (/vk\.com/i.test(url)) return 'VK'
  if (/tiktok\.com/i.test(url)) return 'TIKTOK'
  if (/linkedin\.com/i.test(url)) return 'LINKEDIN'
  if (/(twitter|x)\.com/i.test(url)) return 'TWITTER'
  return ''
}

function extract(apiData: any): { matches: Match[]; indexSize?: number } {
  const matches: Match[] = []
  let indexSize: number | undefined
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  entities.forEach((e) => {
    const rows = Array.isArray(e?.data) ? e.data : []
    rows.forEach((r: any) => {
      if (!r || typeof r !== 'object') return
      if (r.indexDB) indexSize = Number(r.indexDB)
      matches.push({
        score: Number(r.score ?? 0),
        image: r.base64 || r.thumbnail || r.image_url,
        url: r.url,
        name: r.name || r.title,
        index: r.index,
        guid: r.guid,
      })
    })
  })
  return { matches: matches.sort((a, b) => b.score - a.score), indexSize }
}

export function FaceTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const { matches, indexSize } = React.useMemo(() => extract(apiData), [apiData])
  const { openAt, lightbox } = usePhotoLightbox(matches.map((m) => m.image))
  const query = (apiData as any)?.criteria

  if (!matches.length) {
    return <NoData title="No similar faces found" hint="Try a higher-resolution, front-facing image." />
  }

  const best = matches[0]
  const strong = matches.filter((m) => m.score >= 80).length

  return (
    <>
      <ResCard title="Query image and outcome">
        <Box sx={{ display: 'flex', gap: '14px', alignItems: 'stretch', flexWrap: 'wrap' }}>
          <Box sx={{ width: 132, alignSelf: 'stretch', minHeight: 96, borderRadius: `${radii.card}px`, background: tk.surface3, flex: 'none', display: 'grid', placeItems: 'center', overflow: 'hidden', color: tk.text3 }}>
            {typeof query === 'string' && query.startsWith('data:image') ? (
              <Box component="img" src={query} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="12" cy="9" r="3.4" /><path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" /></svg>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Stats
              items={[
                { label: 'Matches', value: matches.length, sub: 'returned', tone: strong ? 'ok' : 'warn' },
                { label: 'Best score', value: `${best.score}%`, sub: band(best.score).label, tone: best.score >= 80 ? 'ok' : 'warn' },
                { label: 'Strong (80%+)', value: strong, tone: strong ? 'ok' : undefined },
                ...(indexSize ? [{ label: 'Index searched', value: `${(indexSize / 1e9).toFixed(2)} B`, sub: 'faces' }] : []),
              ]}
            />
          </Box>
        </Box>
      </ResCard>

      <ResCard title="Similar faces" count={matches.length}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '11px' }}>
          {matches.map((m, i) => {
            const b = band(m.score)
            const net = networkFromUrl(m.url)
            return (
              <Box key={i} sx={{ border: `1px solid ${tk.border}`, borderRadius: `${radii.card}px`, background: tk.surface2, overflow: 'hidden' }}>
                <Box sx={{ aspectRatio: '1', background: tk.surface3, position: 'relative', display: 'grid', placeItems: 'center', color: tk.text3 }}>
                  {m.image ? (
                    <Photo value={m.image} onClick={() => openAt(i)} />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="12" cy="9" r="3.4" /><path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" /></svg>
                  )}
                  <Box sx={{ position: 'absolute', top: 8, left: 8, fontSize: 11, fontWeight: 800, px: '8px', py: '3px', borderRadius: '7px', color: '#fff', background: b.colour }}>
                    {m.score}% {b.label}
                  </Box>
                </Box>
                <Box sx={{ p: '10px 11px' }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name || (net ? networkMeta(net).label : 'Unnamed match')}
                  </Typography>
                  <Box sx={{ height: 4, borderRadius: '20px', background: tk.surface3, overflow: 'hidden', my: '7px' }}>
                    <Box sx={{ height: '100%', width: `${m.score}%`, borderRadius: '20px', background: b.colour }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {net && (
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: '8px', py: '3px', borderRadius: '7px', background: networkMeta(net).color, color: '#fff' }}>
                        {networkMeta(net).label}
                      </Box>
                    )}
                    {m.url && (
                      <Box component="a" href={m.url} target="_blank" rel="noreferrer" sx={{ ml: 'auto', fontSize: 11, color: '#2D89F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        open source
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>

        {best.score < 80 && (
          <Box sx={{ mt: '12px' }}>
            <Warn title={`${best.score}% is below the identification threshold`}>
              Treat this as a lead, not a match. Scores under 80% from a single source are frequently coincidental,
              especially on low-resolution images.
            </Warn>
          </Box>
        )}
      </ResCard>

      <RawCard data={apiData} />
      {lightbox}
    </>
  )
}
